<script setup lang="ts">
import { SancareBadge, SancareOcticon, SancareToggle } from '@sancare/typescript-commons'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import { roleLabels } from '@/misc/constants'
import { hasPermission, RoleEnum } from '@/misc/permissions/permissionsHelper'
import { User } from '@/store/modules/settings/types'

import UserAssignationModal from './UserAssignationModal.vue'
import UserEditModal from './UserEditModal.vue'

const store = useStore()
const { t } = useI18n()

const showEditor = ref(false)
const showAssignation = ref(false)
const userIdToDelete = ref<number | null>(null)
const editedUser = ref<User | null>(null)
const searchQuery = ref('')
const showDisabledUsers = ref(false)

const userList = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()

  return store.state.admin.userList
    .filter((user: User) => showDisabledUsers.value || ! user.disabled)
    .filter((user: User) => ! query || [user.username, user.firstName, user.lastName, user.email]
      .some((field) => field?.toLowerCase().includes(query)))
    .slice()
    .sort((a, b) => a.id - b.id)
})

const currentUserId = computed(() => store.state.login.currentUser.id)
const editableRoles = computed(() => store.state.login.currentUser.editableRoles)
const createUserOk = computed(() => store.state.admin.createUserRequest.ok)
const deleteUserOk = computed(() => store.state.admin.deleteUserRequest.ok)
const updateUserOk = computed(() => store.state.admin.updateUserRequest.ok)
const enableUserOk = computed(() => store.state.admin.enableUserRequest.ok)
const userRole = computed(() => store.getters.userRole)

watch(createUserOk, (ok) => {
  if (ok) {
    closeEditor()
  }
})

watch(updateUserOk, (ok) =>  {
  if (ok) {
    if (editedUser.value.id === currentUserId.value) {
      store.dispatch('getSelf')
    }
    closeEditor()
  }
})

watch(deleteUserOk, (ok) =>  {
  if (ok) {
    userIdToDelete.value = null
    refresh()
  }
})
watch(enableUserOk, (ok) =>  {
  if (! ok) {
    return
  }

  refresh()
})

watch(userRole, () => {
  refresh()
})

onMounted(() => {
  refresh()
})

function refresh() {
  store.dispatch('getUserList')
}

function openEditor(user: User | null = null) {
  showEditor.value = true
  editedUser.value = user ? { ...user } : null
}

function closeEditor() {
  showEditor.value = false
  if (editedUser.value) {
    store.commit('resetUpdateUserRequest')
    editedUser.value = null
  } else {
    store.commit('resetCreateUserRequest')
  }
}

function openAssignation(user: User) {
  showAssignation.value = true
  editedUser.value = { ...user }
}

function closeAssignation() {
  showAssignation.value = false
}

function handleUserModal(payload) {
  store.dispatch(editedUser.value ? 'updateUser' : 'createUser', payload)
}

function deleteUser() {
  store.dispatch('deleteUser', userIdToDelete.value)
}

function enableUser(userId: number) {
  store.dispatch('enableUser', userId)
}

function canEditUser(user) {
  return hasPermission(RoleEnum.ROLE_USER_MANAGER, userRole.value)
    && user.disabled === false
    && editableRoles.value.includes(user.role)
}

function hasFiness(user: User): boolean {
  return user.legalEntities.length > 0 || user.geographicEntities.length > 0
}

function finessTooltip(user: User): string {
  if (! hasFiness(user)) {
    return t('user.finessAssignations')
  }

  return [...user.legalEntities, ...user.geographicEntities]
    .map((finess) => finess.shortDescription || finess.finess)
    .join(', ')
}
</script>

<template>
  <div>
    <div class="px-3 row mt-3">
      <div class="col-6">
        <h2>{{ t('user.user', 2) }}</h2>
      </div>
    </div>
    <div class="px-3 row align-items-center mb-2">
      <div class="col-4">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          :placeholder="t('user.search')"
        >
      </div>
      <div class="col-auto">
        <sancare-toggle
          :label="t('user.showDisabledUsers')"
          :value="showDisabledUsers"
          @toggle-change="showDisabledUsers = !showDisabledUsers"
        />
      </div>
    </div>
    <div class="px-3">
      <table class="sancare-data-table stripped mt-3">
        <thead>
          <tr>
            <th>{{ t('user.username') }}</th>
            <th>{{ t('user.lastName') }} - {{ t('user.firstName') }}</th>
            <th>{{ t('user.email') }}</th>
            <th>{{ t('user.role') }}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="user in userList"
            :key="user.username"
            :class="{ disabled: user.disabled }"
          >
            <td>
              {{ user.username }}
              <sancare-badge
                v-if="user.createdFromLdap"
                v-tooltip="t('user.authBadge.ldapTooltip')"
                :text="t('user.authBadge.ldap')"
                class="ms-1"
              />
              <sancare-badge
                v-if="user.createdFromOidc"
                v-tooltip="t('user.authBadge.ssoTooltip')"
                :text="t('user.authBadge.sso')"
                variant="info"
                class="ms-1"
              />
            </td>
            <td>{{ user.lastName }} {{ user.firstName }}</td>
            <td>{{ user.email }}</td>
            <td>{{ roleLabels[user.role] }}</td>
            <td>
              <div
                v-if="canEditUser(user)"
                class="row g-0"
              >
                <div class="col-auto">
                  <button
                    v-tooltip="t('user.editUser')"
                    class="btn btn-link"
                    @click="openEditor(user)"
                  >
                    <sancare-octicon
                      name="pencil"
                      :width="15"
                      :height="15"
                    />
                  </button>
                </div>
                <div class="col-auto">
                  <button
                    v-tooltip="finessTooltip(user)"
                    class="btn btn-link"
                    :class="{ 'btn-link--finess-active': hasFiness(user) }"
                    @click="openAssignation(user)"
                  >
                    <sancare-octicon
                      name="organization"
                      :width="15"
                      :height="15"
                    />
                  </button>
                </div>
                <v-dropdown
                  v-if="user.id !== currentUserId"
                  :shown="userIdToDelete === user.id"
                  placement="right"
                  class="col-auto"
                  @hide="userIdToDelete = null"
                >
                  <button
                    v-tooltip="t('user.deleteUser')"
                    class="btn btn-link"
                    @click="userIdToDelete = user.id"
                  >
                    <sancare-octicon
                      name="trash"
                      :width="15"
                      :height="15"
                    />
                  </button>
                  <template #popper>
                    <div class="p-3">
                      <h4 class="title-light text-center">
                        {{ t('areYouSure') }}
                      </h4>
                      <div class="text-center">
                        <button
                          class="btn btn-danger btn-padded"
                          @click="deleteUser()"
                        >
                          {{ t('delete') }}
                        </button>
                      </div>
                    </div>
                  </template>
                </v-dropdown>
              </div>
              <div
                v-if="hasPermission(RoleEnum.ROLE_ADMIN_MANAGER, userRole)
                  && hasPermission(RoleEnum.ROLE_SYSTEM_USER_MANAGER, userRole)
                  && user.disabled === true"
                class="row g-0"
              >
                <button
                  v-tooltip="t('user.activateUser')"
                  class="btn btn-link"
                  @click="enableUser(user.id)"
                >
                  <sancare-octicon
                    name="zap"
                    :width="15"
                    :height="15"
                  />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="px-3 row g-0 mt-3 mb-3">
      <div class="col text-end">
        <button
          class="btn btn-push btn-primary btn-padded"
          @click="openEditor()"
        >
          {{ t('user.newUser') }}
        </button>
      </div>
    </div>
    <user-edit-modal
      :show-editor="showEditor"
      :edited-user="editedUser"
      @submit-form="handleUserModal"
      @close-modal="closeEditor"
    />
    <user-assignation-modal
      v-if="editedUser !== null"
      :show-modal="showAssignation"
      :user-id="editedUser.id"
      @close-modal="closeAssignation"
    />
  </div>
</template>
<style>
.sancare-data-table .btn-link--finess-active svg path {
  fill: #05509e;
}
</style>
